import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import { supabase } from "../lib/supabase";
import { useLang } from "../context/LangContext";
import { IMAGES } from "../lib/images";
import api from "../lib/api";

export default function Login() {
  const { t, lang, toggle } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    if (forgot) {
      try {
        await api.post("/auth/forgot-password", { email: email.trim(), lang });
      } catch (err) { /* always show sent */ }
      setBusy(false);
      return setSent(true);
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (error) return setMsg(error.message);
    navigate(from, { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="login-page">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="w-full max-w-md mx-auto">
          <div className="flex items-center justify-between mb-12">
            <Link to="/" className="flex items-center gap-2.5 group" data-testid="login-logo">
              <div className="flex h-8 w-8 items-center justify-center bg-[#0A0F1A] rounded-[4px] group-hover:bg-emerald-500 transition-colors">
                <QrCode size={18} weight="bold" className="text-white" />
              </div>
              <span className="font-display font-bold text-[17px] tracking-tight">Certifico<span className="text-emerald-500">QR</span></span>
            </Link>
            <button onClick={toggle} className="font-mono-tech text-xs font-semibold border border-slate-300 rounded-[4px] px-2 py-1" data-testid="login-lang-toggle">{lang.toUpperCase()}</button>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
            <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0F172A]">{forgot ? t.auth.forgotTitle : t.auth.loginTitle}</h1>
            <p className="mt-3 text-slate-600">{forgot ? t.auth.forgotSub : t.auth.loginSub}</p>

            <form onSubmit={submit} className="mt-10 space-y-5">
              <div>
                <label className="block font-mono-tech text-xs uppercase tracking-wider text-slate-500 mb-2">{t.auth.email}</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it"
                  className="w-full rounded-md border border-slate-300 px-4 py-3 text-[#0F172A] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition" data-testid="login-email-input" />
              </div>
              {!forgot && (
                <div>
                  <label className="block font-mono-tech text-xs uppercase tracking-wider text-slate-500 mb-2">{t.auth.password}</label>
                  <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                    className="w-full rounded-md border border-slate-300 px-4 py-3 text-[#0F172A] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition" data-testid="login-password-input" />
                </div>
              )}
              {msg && <p className="text-sm text-red-600" data-testid="login-error">{msg}</p>}
              {sent && <p className="text-sm text-emerald-600" data-testid="forgot-sent">{t.auth.forgotSent}</p>}
              <button type="submit" disabled={busy || (forgot && sent)} className="w-full rounded-full bg-emerald-500 py-3.5 font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors disabled:opacity-60" data-testid="login-submit">
                {busy ? "…" : forgot ? t.auth.forgotCta : t.auth.login}
              </button>
            </form>

            <button type="button" onClick={() => { setForgot(!forgot); setMsg(""); setSent(false); }}
              className="mt-5 text-sm font-medium text-slate-500 hover:text-emerald-600 transition-colors" data-testid="login-forgot-toggle">
              {forgot ? t.auth.backToLogin : t.auth.forgotLink}
            </button>

            <p className="mt-8 text-sm text-slate-600">
              {t.auth.noAccount} <Link to="/register" className="font-semibold text-emerald-600 hover:text-emerald-700" data-testid="login-to-register">{t.auth.signup}</Link>
            </p>
            <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F172A] transition-colors" data-testid="login-back">
              <ArrowLeft size={16} /> {t.auth.back}
            </Link>
          </motion.div>
        </div>
      </div>

      <div className="relative hidden lg:block">
        <img src={IMAGES.authFactory} alt="Industrial facility" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0A0F1A]/60" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="flex items-center gap-2 text-emerald-400 mb-4">
            <ShieldCheck size={22} weight="fill" />
            <span className="font-mono-tech text-xs uppercase tracking-[0.2em]">UE 2023/1230</span>
          </div>
          <p className="font-display font-bold text-3xl text-white max-w-md leading-tight">{t.hero.title1} {t.hero.title2} {t.hero.title3}</p>
        </div>
      </div>
    </div>
  );
}
